import {
  carryCombatResults,
  holdCombatResultsForMelee,
  recordRoundStandings,
  requestRuinsIncomeAnimation,
  releaseCombatResultsForMelee,
  showCityCaptureModal,
  showCombatResultsModal,
  showGameEndModal,
  showInitiativeModal,
  showRoundEndModal,
} from '../actions';
import {
  getRuinsIncomeRoundLimit,
  getRuinsIncreasedIncomeStartRound,
} from '../../utils/gameState/income/checkIfRoundWithIncreasedRuinsIncome';
import {PHASES} from '../../constants/phases';
import {buildRoundStandings} from '../../utils/gameEnd/buildRoundStandings';
import {resolveCombatStats} from '../../utils/gameState/buildCombatStats';
import {checkIfMeleeFollowsStage} from '../../utils/gameState/turn/checkIfMeleeFollowsStage';

const IGNORED_ACTIONS = ['LOAD_STATE', 'RESET_STATE'];

function getGameState(state) {
  return state.shared?.gameState;
}

export default function modalsMiddleware(store) {
  return (next) => (action) => {
    const prevState = store.getState();
    const result = next(action);

    const nextState = store.getState();
    const prevShared = getGameState(prevState);
    const nextShared = getGameState(nextState);

    if (!prevShared || !nextShared) {
      return result;
    }

    // Итоги партии могут приехать и полным состоянием: переподключение к уже
    // завершенному матчу и ресинк после отклоненного устаревшего действия
    // (STALE_ACTION_REJECTED -> LOAD_STATE) приносят gameResults разом со всем
    // остальным. Обычная ветка ниже до них не доходит — LOAD_STATE выходит
    // раньше, чтобы ресинк не сыпал модалками конца раунда и боя, — и игрок
    // оставался в законченном матче вообще без модалки итогов
    const hasFreshGameResults = nextShared.gameResults?.length > 0
      && nextShared.gameResults !== prevShared.gameResults
      && !nextState.private?.tutorial?.chapterId;

    //nextShared.isGameStarted
    if (action.type === 'LOAD_STATE') {
      if (hasFreshGameResults) {
        store.dispatch(showGameEndModal());
      }
      if (nextShared.phase === PHASES.SETUP || nextShared.phase === PHASES.FIELD_PREPARATION) {
        store.dispatch(showInitiativeModal());
      }
      return result;
    }

    if (IGNORED_ACTIONS.includes(action.type)) {
      return result;
    }

    const prevCapturedCities = prevShared.lastCapturedCities;
    const nextCapturedCities = nextShared.lastCapturedCities;
    if (nextCapturedCities?.length > 0 && nextCapturedCities !== prevCapturedCities) {
      store.dispatch(showCityCaptureModal());
    }

    const prevCombatResults = prevShared.combatResults;
    const nextCombatResults = nextShared.combatResults;
    const isCombatResultsChanged = nextCombatResults !== prevCombatResults;
    // Каждый этап заменяет итоги своими, в том числе пустыми: то, что игрок еще не
    // закрыл, остается у него и покажется вместе с новым
    if (isCombatResultsChanged && prevCombatResults?.length > 0
      && action.type !== 'CLEAR_COMBAT_RESULTS'
      && prevState.private?.modalVisibility?.showCombatResultsModal) {
      store.dispatch(carryCombatResults({
        results: prevCombatResults,
        stats: resolveCombatStats(prevShared.combatStats, prevCombatResults, prevShared.players),
      }));
    }
    if (nextCombatResults?.length > 0 && isCombatResultsChanged) {
      // Сначала решается, ждать ли боя: флаг показа не должен поднять модалку ни на кадр
      const stage = nextShared.warPhaseStage;
      if (nextShared.phase === PHASES.WAR && checkIfMeleeFollowsStage(nextState, stage)) {
        store.dispatch(holdCombatResultsForMelee({
          stage,
          playerId: nextShared.players[nextShared.currentPlayerIndex].id,
        }));
      }
      store.dispatch(showCombatResultsModal());
    }
    // Гибель отрядов боя к этому моменту уже подняла свой флаг и подержит модалку сама
    if (action.type === 'APPLY_MELEE_DAMAGE' && nextState.private?.modalVisibility?.combatResultsMeleeHold) {
      store.dispatch(releaseCombatResultsForMelee());
    }

    // В обучении итоги партии не показываем вовсе: главу завершает
    // поздравление TutorialChapterCompleteModal, и обычная модалка конца игры
    // (со списком мест и кнопками выхода) только мешала бы ему
    if (hasFreshGameResults) {
      store.dispatch(showGameEndModal());
    }

    const prevIsGameStarted = prevShared.isGameStarted;
    const nextIsGameStarted = nextShared.isGameStarted;
    const nextPhase = nextShared.phase;
    if (nextIsGameStarted && !prevIsGameStarted && nextPhase === PHASES.SETUP) {
      store.dispatch(showInitiativeModal());
    }

    const prevRound = prevShared.round;
    const nextRound = nextShared.round;
    if (nextRound > prevRound && nextRound > 1) {
      // Слепок итогов пишется и тогда, когда сама модалка скрыта настройкой:
      // иначе при следующем показе сравнивать было бы не с чем
      store.dispatch(recordRoundStandings(prevRound, buildRoundStandings(nextShared), nextShared.startedAt));
      store.dispatch(showRoundEndModal());
    }

    // Окно считается от лимита раундов партии; без лимита окна нет (null).
    // Если окно с первого раунда, доход не растет: «было» не существовало
    const ruinsIncomeRound = getRuinsIncreasedIncomeStartRound(getRuinsIncomeRoundLimit(nextShared));
    if (ruinsIncomeRound !== null && ruinsIncomeRound > 1 && nextRound === ruinsIncomeRound && nextRound !== prevRound) {
      // В обучении роста дохода не показываем вовсе: сценарии глав про другое
      if (!nextState.private?.tutorial?.chapterId) {
        store.dispatch(requestRuinsIncomeAnimation());
      }
    }

    return result;
  };
}
